// Centralised brand + content data for the whole site.

export const BRAND = {
  name: "Enndo Web Solution",
  short: "Enndo",
  tagline: "Web solutions and digital products for founders, businesses & shops",
  mission:
    "We build any website or digital product you need - startup websites, business websites, online stores, custom platforms, internal tools, and AI-powered apps. Built to industry standards by engineers from Top MNCs and IIT alumni - properly coded, built to scale, and secured from day one. Not AI-generated, not vibe-coded, not a prompt.",
  founded: 2024,
  email: process.env.REACT_APP_CONTACT_EMAIL || "enndowebsolution@gmail.com",
  phone: process.env.REACT_APP_CONTACT_PHONE || "+91-9082164270",
  whatsapp: process.env.REACT_APP_WHATSAPP_NUMBER || "+91-9082164270",
  city: "India · Remote-first",
  directorName: "Neelam Vishvakarma",
  directorTitle: "Technical Consultant",
  directorPhone: "+91-9205354432 (WhatsApp Only)",
};

export const STATS = [
  { value: "10+",    label: "Websites & digital products delivered" },
  { value: "12k+",   label: "Active users served daily" },
  { value: "99.9%",  label: "Uptime across products" },
  { value: "100%",   label: "Full code & account ownership" },
];

export const SERVICES = [
  {
    id: "websites",
    icon: "🏢",
    title: "Websites for Startups, Businesses & Shops",
    blurb:
      "A clean, fast, professional website that represents your business properly - built for your brand, not picked from a template library.",
    points: ["Custom design for your brand", "Works great on phone & desktop", "Fast, SEO-ready, and secure"],
    accent: "linear-gradient(135deg,#6366f1,#a855f7)",
  },
  {
    id: "ecommerce",
    icon: "🛒",
    title: "Online Stores & Marketplaces",
    blurb:
      "From a small shop to a multi-vendor marketplace, we build stores that handle real orders, real payments, and real traffic without breaking.",
    points: ["Secure online payments", "Inventory & order tracking", "Multi-vendor support if you need it"],
    accent: "linear-gradient(135deg,#22d3ee,#6366f1)",
  },
  {
    id: "saas-platforms",
    icon: "🚀",
    title: "Custom Web Platforms & SaaS Products",
    blurb:
      "Got an idea for a product, not just a website? We build it - user accounts, payments, admin panel, and everything in between.",
    points: ["Login & user accounts", "Plans, billing & payments", "Admin dashboard to manage it all"],
    accent: "linear-gradient(135deg,#a855f7,#ec4899)",
  },
  {
    id: "edtech",
    icon: "🎓",
    title: "Ed-Tech & Course Platforms",
    blurb:
      "We've built and run the PrrepX ecosystem ourselves - live classes, test series, doubt support, and student dashboards, used by thousands daily.",
    points: ["Live class & course portals", "Online test & assessment engines", "Student progress dashboards"],
    accent: "linear-gradient(135deg,#f59e0b,#ef4444)",
  },
  {
    id: "internal-tools",
    icon: "🧩",
    title: "Internal Tools, HRMS & Employee Portals",
    blurb:
      "Replace messy spreadsheets with a proper internal system - attendance, payroll, tasks, and reports, all in one place for your team.",
    points: ["Attendance & payroll automation", "Task & team management", "Role-based access for your staff"],
    accent: "linear-gradient(135deg,#0ea5e9,#10b981)",
  },
  {
    id: "ai",
    icon: "🤖",
    title: "AI Features & Automation",
    blurb:
      "Add real AI features that save you time - smart assistants, auto replies, document generation, and workflow automation built into your product.",
    points: ["AI chat & smart assistants", "Automated documents & reports", "WhatsApp, email & voice automation"],
    accent: "linear-gradient(135deg,#ec4899,#a855f7)",
  },
  {
    id: "security",
    icon: "🛡️",
    title: "Secure, Reliable Engineering Behind Everything",
    blurb:
      "Every project we build runs on a properly secured backend, safe logins, and reliable hosting - so your business and your customers' data stay protected.",
    points: ["Secure logins & databases", "SSL, backups & monitoring", "Built to handle real traffic"],
    accent: "linear-gradient(135deg,#ef4444,#f59e0b)",
  },
  {
    id: "manage",
    icon: "⚙️",
    title: "Ongoing Support & Maintenance",
    blurb:
      "Launch day is just the start. We stay available for fixes, updates, new features, and monthly support - for as long as you need us.",
    points: ["Monthly support plans", "Fast response when something breaks", "New features whenever you need them"],
    accent: "linear-gradient(135deg,#10b981,#22d3ee)",
  },
];

export const PROCESS = [
  { step: "01", title: "Discover",  desc: "A free call to understand what you need, your budget, and your timeline." },
  { step: "02", title: "Plan",      desc: "We map out the pages, features, and structure before any building starts." },
  { step: "03", title: "Build",     desc: "Our team builds and tests everything properly - design, backend, and security." },
  { step: "04", title: "Launch",    desc: "We put it live on your domain with SSL, and make sure everything works." },
  { step: "05", title: "Support",   desc: "Ongoing support and updates - we don't disappear after launch." },
];

export const PORTFOLIO = [
  {
    id: "sn-india-group",
    title: "SN India Group",
    type: "Multi-Vertical Corporate Platform",
    description:
      "Corporate platform for a multi-vertical startup spanning industrial material supply, skilled manpower staffing, engineering & design services, travel & trek booking, and smart home solutions - with a service-request pipeline and role-based admin.",
    href: "https://snindiagroup.online/",
    accent: "linear-gradient(135deg,#1e3a8a,#b45309)",
    badges: ["Multi-vertical business", "Service-request system", "Role-based admin"],
    metric: "Corporate Platform",
  },
  {
    id: "banaras-saree",
    title: "Lumyrra Vault",
    type: "E-Commerce System",
    description:
      "Production e-commerce system for a traditional Banarasi saree brand — full catalog management, secure checkout, and order pipelines.",
    href: "#",
    accent: "linear-gradient(135deg,#b45309,#dc2626)",
    badges: ["Catalog engine", "Secure checkout", "Order pipelines"],
    metric: "E-Commerce System",
  },
  {
    id: "prepx-pathshala",
    title: "PrrepX Pathshala",
    type: "Ed-Tech Platform",
    description:
      "Full-stack learning platform built for PrrepX Pathshala, serving school and competitive-exam students with live classes, a doubt-resolution engine, and real-time student analytics.",
    href: "https://prrepxpathshala.live/",
    accent: "linear-gradient(135deg,#4f46e5,#a855f7)",
    badges: ["Full-stack platform", "Real-time data", "Role-based access"],
    metric: "Ed-Tech Platform",
  },
  {
    id: "testseries",
    title: "PrrepX Test Series",
    type: "Proctored Assessment Engine",
    description:
      "High-concurrency test engine built for PrrepX Pathshala, handling secure payments, timed assessments, and granular admin controls at scale.",
    href: "https://testseries.prrepxpathshala.live/",
    accent: "linear-gradient(135deg,#22d3ee,#6366f1)",
    badges: ["Payment orchestration", "High concurrency", "Admin controls"],
    metric: "Production Test Engine",
  },
  {
    id: "employee",
    title: "HRMS System",
    type: "HRMS / Internal Ops System",
    description:
      "Custom HRMS built for PrrepX Pathshala, handling attendance, payroll, salary slips, task pipelines, and role-based team records for daily operations.",
    href: "#",
    accent: "linear-gradient(135deg,#0ea5e9,#10b981)",
    badges: ["HRMS", "Role-based access", "Automated reports"],
    metric: "Internal Ops System",
  },
  {
    id: "prrepx-counselling",
    title: "eCounselling Portal",
    type: "AI-Integrated Application",
    description:
      "AI-driven counselling engine built for PrrepX Pathshala, running predictive models for JEE/NEET college admission and rank analysis at scale.",
    href: "https://ecounselling.prrepxpathshala.live/",
    accent: "linear-gradient(135deg,#0284c7,#6366f1)",
    badges: ["AI/ML models", "Predictive engine", "Data pipelines"],
    metric: "AI Product",
  },
  {
    id: "prrepx-tools",
    title: "Internal Management Tools",
    type: "AI Document Automation Tool",
    description:
      "AI-powered document engine built for PrrepX Pathshala that generates, edits, and renders complex PDFs and math notation with production-grade reliability.",
    href: "#",
    accent: "linear-gradient(135deg,#a855f7,#22d3ee)",
    badges: ["AI automation", "Document engine", "High-fidelity rendering"],
    metric: "AI Automation Tool",
  },
  {
    id: "laado",
    title: "Laado Ki Shaadi",
    type: "Vendor Marketplace",
    description:
      "Multi-vendor booking marketplace connecting couples with wedding service providers, engineered for a seamless mobile-first experience.",
    href: "#",
    accent: "linear-gradient(135deg,#ec4899,#f59e0b)",
    badges: ["Marketplace logic", "Vendor management", "Mobile-first"],
    metric: "Marketplace Platform",
  },
  {
    id: "your-project",
    title: "Your Product Here",
    type: "Coming Soon",
    description:
      "Tell us what you're building — a SaaS MVP, marketplace, internal system, or AI-integrated product. We'll architect it right.",
    href: "/contact",
    accent: "linear-gradient(135deg,#10b981,#3b82f6)",
    badges: ["You", "+ Us", "= 🚀"],
    metric: "Let's build it",
    cta: true,
  },
];

export const HOW_WE_HELP = [
  {
    icon: "⚡",
    title: "Built to Handle Growth",
    desc: "Whether you have 10 customers or 10 lakh, your website or app keeps working - no slowdowns, no crashes.",
  },
  {
    icon: "🛠️",
    title: "Real Engineers, Not Prompts",
    desc: "Every line is written and reviewed by experienced engineers, following industry-standard practices - not AI-generated, not vibe-coded, not a shortcut.",
  },
  {
    icon: "🔧",
    title: "Built for You, Not a Template",
    desc: "No page builders, no copy-paste templates. Every website or product is built around exactly what your business needs.",
  },
  {
    icon: "🔒",
    title: "Secure From Day One",
    desc: "Safe logins, protected data, and secure payments - your customers' information is always protected.",
  },
  {
    icon: "🤖",
    title: "AI When You Actually Need It",
    desc: "Chatbots, automation, smart tools - we add AI features that genuinely save you time, not just for show.",
  },
  {
    icon: "🔑",
    title: "You Own Everything",
    desc: "Full code on GitHub, every login and account handed to you. It's 100% yours, no strings attached.",
  },
];

export const DELIVERABLES = [
  { title: "Full Source Code Handover", desc: "Delivered on GitHub with 100% ownership - it's yours, not licensed to you." },
  { title: "All Logins & Accounts", desc: "Every hosting account, database, and third-party login is handed to you directly." },
  { title: "Secure, Well-Built Backend", desc: "Safe logins, protected data, and a backend built to handle real users properly." },
  { title: "Live on Your Own Domain, With SSL", desc: "Your website or app goes live on your domain with proper security in place." },
  { title: "A Live, Working Link", desc: "Not a demo or a draft - a real, working product your customers can use." },
  { title: "1-on-1 Walkthrough Call", desc: "We walk you through exactly how everything works, in plain language." },
  { title: "Ongoing Support, Whenever You Need It", desc: "Support and monthly plans available for as long as you want to keep working with us." },
];

export const PRICING = [
  {
    id: "foundation",
    name: "Foundation",
    tagline: "A serious first website for founders, professionals & small businesses",
    priceFrom: "₹24,999",
    priceNote: "one-time",
    highlight: false,
    accent: "linear-gradient(135deg,#22d3ee,#6366f1)",
    features: [
      "Built around exactly what your business needs and growth with full security and flexibility",
      "Solves the real problem: giving visitors a reason to trust you and contact you",
      "Every page earns its place - nothing there just to look busy",
      "A simple admin dashboard to track enquiries and manage your content yourself",
      "Full dynamic, customized, secured and scalable website for your exact needs",
      "30 days of free support to fine-tune anything after launch",
    ],
  },
  {
    id: "growth",
    name: "Growth",
    tagline: "For businesses ready for real growth - Powerful Backened, Authentications & Dashboards",
    priceFrom: "₹49,999",
    priceNote: "starting",
    highlight: true,
    accent: "linear-gradient(135deg,#a855f7,#ec4899)",
    badge: "Most Popular",
    features: [
      "Fully secure, flexible, powerful & customized around how your business actually sells and operates",
      "Solves the real bottleneck - customers can browse, pay & order without calling you",
      "An admin dashboard built for your workflow, so you can track and manage your business yourself",
      "Automated alerts mean no order or enquiry ever slips through the cracks",
      "Built to scale - more products, more customers, more traffic, without breaking",
      "First 1.5 months managed by us, completely free, while you settle in",
    ],
  },
  {
    id: "enterprise",
    name: "Enterprise",
    tagline: "For platforms, marketplaces & founders who need a long-term technology partner",
    priceFrom: "Custom",
    priceNote: "let's talk",
    highlight: false,
    accent: "linear-gradient(135deg,#f59e0b,#ef4444)",
    features: [
      "Built entirely around your business model - Secure authentications, customized workflows, and scalable architecture",
      "Solves the operational problems generic software can't - your workflow, your rules",
      "Designed to scale with you, from your first hundred users to your first hundred thousand",
      "Powerful admin dashboards to track and manage every part of your business in real time",
      "Every dashboard, login & feature exists because your business actually needs it",
      "One dedicated project lead who understands your business, not a support ticket queue",
      "First month fully managed by us, free - we prove it scales before you take over",
      "24×7 support with priority response, because at this scale, downtime costs real money",
    ],
  },
];

export const TESTIMONIALS = [
  {
    name: "Aarav S.",
    role: "Founder, early-stage startup",
    quote:
      "They didn't just design a nice-looking site — they built proper logins, a real database, and a smooth launch. Felt like having an in-house tech team, not a freelancer.",
    accent: "#22d3ee",
  },
  {
    name: "Megha R.",
    role: "Founder, D2C commerce brand",
    quote:
      "Our old site crashed during every big sale. Enndo rebuilt our store's order and payment flow — no downtime since launch, even on our biggest sale day.",
    accent: "#a855f7",
  },
  {
    name: "Dr. Karan",
    role: "Director, education institute",
    quote:
      "Lectures, payments, doubt portal, employee management — they built our whole system, properly, from scratch. Worth every rupee.",
    accent: "#ec4899",
  },
  {
    name: "Priya M.",
    role: "Founder, service marketplace",
    quote:
      "They handed over the full source code, every login, and a proper walkthrough call. We finally own our own platform, completely.",
    accent: "#f59e0b",
  },
];

export const FAQS = [
  {
    q: "How is this different from Wix, Shopify, or a freelancer?",
    a: "Page builders and cheap templates break once you get real traffic, and freelancers often disappear after launch. We build it properly - a real backend, secure logins, and a database that can grow with you. And you own 100% of it.",
  },
  {
    q: "Do you only build websites, or other digital products too?",
    a: "Both. Websites for startups, businesses and shops - and also full digital products: online stores, custom platforms, SaaS apps, internal tools, and AI-powered apps. If it's a web-based idea, we can build it.",
  },
  {
    q: "How long does a typical project take?",
    a: "Simple websites: 3 to 7 days. Online stores and custom platforms: 2 to 6 weeks. Larger, more complex products: 6 to 12 weeks.",
  },
  {
    q: "Can you add AI features to my website or product?",
    a: "Yes. Chatbots, auto-replies, smart document tools, and workflow automation - built into your product, not just a chat widget stuck on top.",
  },
  {
    q: "What happens after my website or product goes live?",
    a: "We don't disappear after launch. We offer ongoing support and monthly plans for updates, fixes, and new features whenever you need them.",
  },
  {
    q: "Do we get the full source code and account access?",
    a: "Always. Full source code on GitHub, every hosting and database login, and a 1-on-1 call to walk you through everything in plain language. We can also sign an NDA before our first call.",
  },
];

export const PROJECT_TYPES = [
  "Business or startup website",
  "Online store / e-commerce",
  "Personal portfolio (student / professional)",
  "Custom web app / SaaS product",
  "Ed-tech / course / test platform",
  "HRMS / employee / internal dashboard",
  "AI-powered feature or automation",
  "Restaurant / clinic / local business site",
  "Marketplace / multi-vendor platform",
  "Other / not sure yet - just tell us the idea",
];

export const BUDGET_RANGES = [
  "Foundation plan (from ₹24,999)",
  "Growth plan (from ₹49,999)",
  "Enterprise plan (custom pricing)",
  "Need help deciding",
];

export const TIMELINES = [
  "ASAP (within a week)",
  "2–4 weeks",
  "1–2 months",
  "Flexible / not urgent",
];
